import {useMemo} from 'react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useCustomQuery} from '@salesforce/commerce-sdk-react'
import {useOptionalBrand, toBrandCode} from '../contexts/brand-context'
import {getSiteEnvVarValue, resolveBrandScopedValue} from '../utils/site-config'

/* Client-side view of the Optimizely FX config (site pref → MRT env, exposed via
 * window.__CONFIG__) plus the tree scope (brandSlug, localeId) every Optimizely
 * hook needs. Memoised: resolveBrandScopedValue parses JSON. */
export const useOptimizelyFxConfig = () => {
    const {site, locale} = useMultiSite()
    // The UK app has no BrandProvider and is Garage only
    const brandId = useOptionalBrand()?.brandId || 'garage'
    const {app: appConfig} = getConfig()
    const siteId = site?.id
    const localeId = locale?.id || locale?.alias || 'en-CA'

    // OPTIMIZELY_SDK_KEY is excluded from appConfig; the client reads it from the site
    // preference through the preferences API (listed in fetchablePreferences).
    const {data: sitePreferences} = useCustomQuery(
        {
            options: {
                method: 'GET',
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: '/preferences?c_ids=optimizelySdkKey',
                    apiName: 'preferences'
                }
            },
            rawResponse: false
        },
        {
            enabled: typeof window !== 'undefined',
            staleTime: Infinity
        }
    )
    const rawSdkKey = sitePreferences?.optimizelySdkKey

    return useMemo(() => {
        const sdkKey = resolveBrandScopedValue(rawSdkKey, brandId)
        return {
            enabled:
                getSiteEnvVarValue(appConfig, 'OPTIMIZELY_AB_TESTING_ENABLED', siteId) === 'true' &&
                !!sdkKey,
            sdkKey,
            siteId,
            brandSlug: `${toBrandCode(brandId).toLowerCase()}-web`,
            localeId
        }
    }, [appConfig, rawSdkKey, siteId, brandId, localeId])
}
